import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { fetchMyHome } from '@/lib/api';
import { formatMoney } from '@/lib/money';
import {
  homeRateFromTenant, estimateSessionUtilityCents, describeHomeRate,
  type HomeUtilityRate,
} from '@/lib/homeRate';

/**
 * Home-session framing for the receipt surfaces (SessionDetailPage,
 * SessionCompletePage, the History detail sheet, the receipt PDF).
 *
 * A session at the caller's OWN home charger is never billed by EVI —
 * the honest receipt drops the $0 EVI-billing showcase and instead
 * shows the estimated utility cost (their entered rate applied to the
 * session's kWh), clearly framed as what their utility bills them.
 *
 * Detection: home sessions file under the caller's headless home
 * tenant, so `session.tenant_id === /me/home's tenant _id` is the
 * signal. GET /me/home is cheap, cached under the same ['my-home']
 * key the home pages use, and returns nulls for drivers without a
 * home — safe to query unconditionally.
 *
 * The estimate here prices a single session in isolation (tiered
 * brackets start at 0 month-to-date kWh) — the My Home report owns
 * the month-accumulated view; this is a labeled estimate.
 */
export interface HomeSessionFraming {
  /** True when the session ran at the caller's own home charger. */
  isHomeSession: boolean;
  rate: HomeUtilityRate | null;
  /** Estimated utility cost in cents (fractional), null when the
   *  owner hasn't entered a usable rate. */
  estimateCents: number | null;
  /** "at <this>" phrase for copy ("$0.15/kWh", "your time-of-use
   *  rates"), null when no usable rate. In the home's currency. */
  rateSummary: string | null;
  /** The home's currency (item 3): estimateCents is in it. Null when not
   *  a home session; USD from an Api that predates the field. */
  currency: string | null;
}

export function useHomeSession(
  session: {
    tenant_id?: string;
    started_at: string;
    duration_minutes?: number | null;
    energy_kwh?: number | null;
    cost?: { total: number; currency?: string } | null;
    stripe_payment_intent_id?: string | null;
    billed_to?: 'user' | 'fleet';
  } | null | undefined,
): HomeSessionFraming {
  const homeQ = useQuery({ queryKey: ['my-home'], queryFn: fetchMyHome, staleTime: 60_000 });
  const { i18n } = useTranslation();
  const tenant = homeQ.data?.tenant ?? null;
  // Tenant match alone is NOT enough: /me/home resolves for household
  // MEMBERS too, and a member off the pass list at a MONETIZED home
  // charger is genuinely billed as a guest. "Not billed by EVI" must be
  // factually true, so the framing also requires the session to be
  // unbilled — zero total, no Stripe charge, not fleet-billed. Billed
  // sessions keep the standard receipt.
  const isHomeSession = Boolean(
    session?.tenant_id && tenant && session.tenant_id === tenant._id
    && (session.cost?.total ?? 0) === 0
    && !session.stripe_payment_intent_id
    && session.billed_to !== 'fleet',
  );
  if (!isHomeSession || !session) {
    return { isHomeSession: false, rate: null, estimateCents: null, rateSummary: null, currency: null };
  }
  const rate = homeRateFromTenant(tenant);
  // The owner's electricity rate is in the home's currency (item 3); the
  // session's own currency stands in for an Api that predates the field.
  const currency = homeQ.data?.location?.currency ?? session.cost?.currency ?? 'USD';
  return {
    isHomeSession: true,
    rate,
    estimateCents: estimateSessionUtilityCents(rate, session),
    rateSummary: describeHomeRate(rate, (cents) => formatMoney(cents, currency, i18n.language)),
    currency,
  };
}
